import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { type CustomerApplication, isReviewableApplicationStatus } from '@/lib/business-workflow'
import { listQuestionnaireSourceRecords } from '@/lib/customer-application-binding'
import { reviewCustomerApplication } from '@/lib/data-client/customers'
import { requestApiEnvelope } from '@/lib/data-client/http'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import { useBusinessWorkflowDataClientHydration } from '@/lib/data-client/use-business-workflow-hydration'
import {
  type DirectReviewLinkPayload,
  DirectReviewLinkTokenError,
  buildDirectReviewQuestionnairePath,
  parseDirectReviewPayloadFromSearch,
  resolveDirectReviewApplication,
  resolveDirectReviewSourceRecord,
} from '@/lib/direct-review-links'
import { loadFixture } from '@/lib/mock-api/persistent-store'
import { useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import { ArrowRight, CheckCircle2, FileText, RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

type ApprovalState =
  | { kind: 'loading'; message: string }
  | {
      kind: 'approved'
      application: CustomerApplication
      payload: DirectReviewLinkPayload
    }
  | {
      kind: 'already_approved'
      application: CustomerApplication
      payload: DirectReviewLinkPayload
    }
  | { kind: 'error'; message: string }

export default function DirectReviewApproveRoute() {
  const { isLoading: isAuthLoading } = useAuth()
  const workflowQuery = useBusinessWorkflowDataClientHydration(!isAuthLoading)
  const applications = useBusinessWorkflowStore((state) => state.applications)
  const salesUsers = useBusinessWorkflowStore((state) => state.salesUsers)
  const sourceLinks = useBusinessWorkflowStore((state) => state.sourceLinks)
  const fieldBindings = useBusinessWorkflowStore((state) => state.fieldBindings)
  const createApplicationFromSourceRecord = useBusinessWorkflowStore(
    (state) => state.createApplicationFromSourceRecord,
  )
  const updateApplicationStatus = useBusinessWorkflowStore((state) => state.updateApplicationStatus)
  const [approvalState, setApprovalState] = useState<ApprovalState>({
    kind: 'loading',
    message: '正在處理直接審核通過連結...',
  })
  const hasRun = useRef(false)
  const isHydrating = isAuthLoading || workflowQuery.isLoading || workflowQuery.isFetching

  const sourceRecords = useMemo(() => {
    const snapshot = loadFixture()
    return listQuestionnaireSourceRecords({
      forms: snapshot.forms,
      responses: snapshot.responses,
      sourceLinks,
      applications,
      salesUsers,
      formDetails: snapshot.formDetails,
      fieldBindings,
    })
  }, [applications, fieldBindings, salesUsers, sourceLinks])

  useEffect(() => {
    if (isHydrating) return
    if (hasRun.current) return
    hasRun.current = true

    async function approveDirectLink() {
      try {
        const payload = parseDirectReviewPayloadFromSearch(window.location.search)
        const existingApplication = resolveDirectReviewApplication(applications, payload)
        const sourceRecord = resolveDirectReviewSourceRecord(sourceRecords, payload)
        const apiApplication =
          !existingApplication && payload.applicationId && isApiDataClientMode()
            ? (
                await requestApiEnvelope<CustomerApplication>(
                  `/api/customer-applications/${payload.applicationId}`,
                )
              ).data
            : undefined
        const application =
          existingApplication ??
          apiApplication ??
          (sourceRecord
            ? await createApplicationFromSourceRecord(sourceRecord.id, null)
            : undefined)

        if (!application) {
          setApprovalState({
            kind: 'error',
            message: '找不到符合此加密參數的問卷填寫記錄，未執行審核。',
          })
          return
        }

        if (!isReviewableApplicationStatus(application.status)) {
          setApprovalState({ kind: 'already_approved', application, payload })
          return
        }

        if (apiApplication && isApiDataClientMode()) {
          const approved = await reviewCustomerApplication(
            apiApplication.id,
            'approved',
            '直接審核連結核准：加密參數已對應問卷 ID 與填寫人 Email。',
          )
          setApprovalState({ kind: 'approved', application: approved, payload })
          return
        }

        await updateApplicationStatus(
          application.id,
          'approved',
          {
            email: application.supervisorEmail,
            name: application.supervisorName,
          },
          '直接審核連結核准：加密參數已對應問卷 ID 與填寫人 Email。',
        )
        const approved =
          useBusinessWorkflowStore
            .getState()
            .applications.find((item) => item.id === application.id) ?? application
        setApprovalState({ kind: 'approved', application: approved, payload })
      } catch (error) {
        setApprovalState({
          kind: 'error',
          message:
            error instanceof DirectReviewLinkTokenError
              ? error.message
              : '直接審核通過連結目前無法使用。',
        })
      }
    }

    void approveDirectLink()
  }, [
    applications,
    createApplicationFromSourceRecord,
    isHydrating,
    sourceRecords,
    updateApplicationStatus,
  ])

  if (approvalState.kind === 'loading') {
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center text-sm text-slate-600">
        {approvalState.message}
      </main>
    )
  }

  if (approvalState.kind === 'error') {
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6">
          <Badge variant="destructive">direct link error</Badge>
          <h1 className="mt-4 text-xl font-semibold">審核未完成</h1>
          <p className="mt-2 text-sm text-slate-600">{approvalState.message}</p>
        </div>
      </main>
    )
  }

  const application = approvalState.application
  const questionnairePath = buildDirectReviewQuestionnairePath(approvalState.payload)
  const alreadyApproved = approvalState.kind === 'already_approved'

  return (
    <main className="min-h-dvh bg-slate-100 px-4 py-8 text-slate-950">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
        <section className="overflow-hidden rounded-xl border border-emerald-200 bg-white text-center shadow-lg shadow-slate-200/70">
          <div className="bg-emerald-50 px-6 py-8">
            <div className="mx-auto grid size-16 place-items-center rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-200">
              {alreadyApproved ? (
                <RotateCcw className="size-8" />
              ) : (
                <CheckCircle2 className="size-8" />
              )}
            </div>
            <Badge className="mt-5 bg-emerald-100 text-emerald-800" variant="success">
              審核結果
            </Badge>
            <h1 className="mt-4 text-3xl font-semibold">
              {alreadyApproved ? '審核已完成' : '審核通過完成'}
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              {alreadyApproved
                ? '這筆申請目前已完成審核，不會重複建立或更新客戶資料。'
                : '已核准此申請，系統會依目前問卷內容建立或更新本系統客戶資料，ERP 建檔狀態仍可在客戶管理中維護。'}
            </p>
          </div>
        </section>

        <section className="grid gap-3 md:grid-cols-2">
          <ResultInfo label="客戶" value={application.companyName} />
          <ResultInfo label="問卷" value={application.formTitle} />
          <ResultInfo label="填寫人 Email" value={application.contactEmail} />
          <ResultInfo label="審核狀態" value={getDirectReviewStatusLabel(application.status)} />
        </section>

        <div className="flex flex-wrap justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <Button asChild className="bg-slate-900 text-white hover:bg-slate-800 hover:text-white">
            <a href={questionnairePath}>
              <FileText className="size-4" />
              查看問卷內容
            </a>
          </Button>
          <Button asChild variant="outline">
            <a href="/dashboard/customers">
              查看客戶資料
              <ArrowRight className="size-4" />
            </a>
          </Button>
          <Button asChild variant="ghost">
            <a href="/dashboard/reviews">回審核中心</a>
          </Button>
        </div>
      </div>
    </main>
  )
}

function getDirectReviewStatusLabel(status: CustomerApplication['status']) {
  const labels: Record<CustomerApplication['status'], string> = {
    draft: '草稿',
    pending_review: '待主管審核',
    changes_requested: '主管退回',
    approved: '主管核准',
    approved_pending_erp_filing: '已核准，待 ERP 建檔',
    customer_created: '客戶已建檔',
    duplicate_pending: '重複待處理',
    rejected: '已拒絕',
  }
  return labels[status] ?? status
}

function ResultInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 break-words font-medium text-slate-950">{value}</div>
    </div>
  )
}
